import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InboxContactChatMessage, DefaultCustomField } from 'src/constants/types';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { convertHtmlToText } from 'src/helpers/helpers';
import { useGetDefaultCustomFieldsApi } from 'src/api/customFields';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useSelector } from 'react-redux';
import { ChatProps } from '../types';

export const useChatController = (
  props: ChatProps,
  ref: React.ForwardedRef<unknown>,
) => {
  const {
    scheduledMessages,
    isMobile,
    deleteScheduledMessage,
    updateScheduledMessage,
    isLoadingMoreMessages,
    hasMoreMessages,
    onLoadMoreMessages,
    messages,
  } = props;

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [showScheduledMessages, setShowScheduledMessages] = useState<boolean>(false);
  const previousScrollHeightRef = useRef<number>(0);
  const previousScheduledWithDateCountRef = useRef<number>(0);
  const [inlineEditingMessageId, setInlineEditingMessageId] = useState<string | null>(null);
  const [inlineEditingText, setInlineEditingText] = useState<string>('');
  const [isInlineUpdating, setIsInlineUpdating] = useState<boolean>(false);
  const [defaultCustomFields, setDefaultCustomFields] = useState<DefaultCustomField[]>([]);
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const getDefaultCustomFields = useGetDefaultCustomFieldsApi();
  const selectedCompanyId = useSelector(getCurrentCompanyId);

  useEffect(() => {
    if (!selectedCompanyId) {
      setDefaultCustomFields([]);
      return;
    }

    getDefaultCustomFields({
      companyId: selectedCompanyId,
    }).then(result => {
      setDefaultCustomFields(result.data.items);
    });
  }, [selectedCompanyId, getDefaultCustomFields]);

  useEffect(() => {
    const previousScheduledWithDateCount = previousScheduledWithDateCountRef.current;
    const currentCount = scheduledMessages.length;
    const currentScheduledWithDateCount = scheduledMessages.filter(message =>
      Boolean(message.dateScheduled),
    ).length;

    if (currentCount === 0) {
      setShowScheduledMessages(false);
      previousScheduledWithDateCountRef.current = 0;
      return;
    }

    if (currentScheduledWithDateCount > previousScheduledWithDateCount) {
      setShowScheduledMessages(false);
      requestAnimationFrame(() => {
        if (ref && typeof ref === 'object' && 'current' in ref && ref.current) {
          const chatElement = ref.current as HTMLDivElement;
          chatElement.scrollTop = chatElement.scrollHeight;
        }
      });
    }

    previousScheduledWithDateCountRef.current = currentScheduledWithDateCount;
  }, [scheduledMessages, ref]);

  useEffect(() => {
    if (!inlineEditingMessageId) {
      return;
    }

    const isMessageStillPresent = scheduledMessages.some(message => message.id === inlineEditingMessageId);
    if (!isMessageStillPresent) {
      setInlineEditingMessageId(null);
      setInlineEditingText('');
      setIsInlineUpdating(false);
    }
  }, [scheduledMessages, inlineEditingMessageId]);

  useEffect(() => {
    const _preventTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const isConversationArea = target.closest('[data-conversation-area]') ||
                                 target.closest('.conversation-scroll') ||
                                 target.closest('[ref="conversationRef"]');

      if (!isConversationArea) {
        e.preventDefault();
      }
    };

    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      if (isKeyboardOpen) {
        document.body.addEventListener('touchmove', _preventTouchMove, { passive: false });
      }

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        if (isKeyboardOpen) {
          document.body.removeEventListener('touchmove', _preventTouchMove, false);
        }
      };
    }
  }, [isMobile, isKeyboardOpen]);

  // Dynamic VisualViewport-based vh unit so header stays stable when the OSK opens
  useEffect(() => {
    if (!isMobile) return;

    const vv = window.visualViewport;
    const setVh = () => {
      const height = vv?.height ?? window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // Detect keyboard state based on viewport height
      const windowHeight = window.innerHeight;
      const heightRatio = height / windowHeight;
      const keyboardVisible = heightRatio < 0.75;

      if (keyboardVisible !== isKeyboardOpen) {
        setIsKeyboardOpen(keyboardVisible);
      }
    };

    setVh();
    vv?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);

    return () => {
      vv?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, [isMobile, isKeyboardOpen]);

  // Handle keyboard focus events from ChatFooter
  const handleKeyboardFocus = (isFocused: boolean) => {
    if (isMobile) {
      if (isFocused) {
        setIsKeyboardOpen(true);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 50);
      } else {
        setTimeout(() => {
          setIsKeyboardOpen(false);
        }, 150);
      }
    }
  };

  // Handle scrolling based on keyboard state
  useEffect(() => {
    if (!isMobile) return;

    const timer = setTimeout(() => {
      if (isKeyboardOpen) {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Also scroll any scrollable parent containers to top
        const scrollableContainers = [
          document.documentElement,
          document.body,
          document.querySelector('[data-main-container]'),
          document.querySelector('.MuiStack-root'),
          document.querySelector('.inbox-container'),
        ];

        scrollableContainers.forEach(container => {
          if (container && container.scrollTop > 0) {
            container.scrollTop = 0;
          }
        });
      } else {
        // Scroll chat to bottom when keyboard closes
        if (ref && typeof ref === 'object' && 'current' in ref && ref.current) {
          const chatElement = ref.current as HTMLDivElement;
          chatElement.scrollTop = chatElement.scrollHeight;
        }
      }
    }, isKeyboardOpen ? 100 : 300);

    return () => clearTimeout(timer);
  }, [isKeyboardOpen, isMobile, ref]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMoreMessages || !hasMoreMessages || isLoadingMoreMessages) {
        return;
      }

      const { scrollTop, scrollHeight } = event.currentTarget;

      if (scrollTop <= 50) {
        previousScrollHeightRef.current = scrollHeight;
        onLoadMoreMessages();
      }
    },
    [onLoadMoreMessages, hasMoreMessages, isLoadingMoreMessages],
  );

  useEffect(() => {
    if (
      !isLoadingMoreMessages &&
      ref &&
      typeof ref === 'object' &&
      'current' in ref &&
      ref.current &&
      previousScrollHeightRef.current > 0
    ) {
      const scrollElement = ref.current as HTMLDivElement;
      const newScrollHeight = scrollElement.scrollHeight;
      const scrollDifference = newScrollHeight - previousScrollHeightRef.current;
      scrollElement.scrollTop += scrollDifference;
      previousScrollHeightRef.current = 0;
    }
  }, [isLoadingMoreMessages, messages?.items?.length, ref]);

  const confirmCancelScheduledMessage = (message: InboxContactChatMessage) => {
    openDialog({
      title: 'Cancel Scheduled Message',
      content: 'Are you sure you want to cancel this scheduled message?',
      confirmButtonText: 'Cancel message',
      onConfirm: () => {
        deleteScheduledMessage(message);
      },
    });
  };

  const startInlineEditing = (message: InboxContactChatMessage) => {
    if (isInlineUpdating) {
      return;
    }
    setInlineEditingMessageId(message.id);
    setInlineEditingText(message.text);
  };

  const cancelInlineEditing = () => {
    if (isInlineUpdating) {
      return;
    }
    setInlineEditingMessageId(null);
    setInlineEditingText('');
    setIsInlineUpdating(false);
  };

  const getInlineEditState = (message: InboxContactChatMessage) => {
    const nextText = convertHtmlToText(inlineEditingText).trim();
    const originalText = convertHtmlToText(message.text).trim();
    const canSave =
      !!nextText &&
      nextText !== originalText &&
      !!message.dateScheduled &&
      !isInlineUpdating;
    return { nextText, originalText, canSave };
  };

  const saveInlineEditing = async (message: InboxContactChatMessage) => {
    const { nextText, canSave } = getInlineEditState(message);
    if (!canSave || !message.dateScheduled) {
      return;
    }

    setIsInlineUpdating(true);
    try {
      await updateScheduledMessage(message, message.dateScheduled, nextText);
      setInlineEditingMessageId(null);
      setInlineEditingText('');
    } finally {
      setIsInlineUpdating(false);
    }
  };

  return {
    ConfirmationDialog,
    defaultCustomFields,
    showScheduledMessages,
    setShowScheduledMessages,
    inlineEditingMessageId,
    inlineEditingText,
    setInlineEditingText,
    isInlineUpdating,
    handleScroll,
    handleKeyboardFocus,
    confirmCancelScheduledMessage,
    startInlineEditing,
    cancelInlineEditing,
    getInlineEditState,
    saveInlineEditing,
  };
};
